import {
  ErrorResponse,
  isErrorResponse,
  ImageWithPlaceholder,
  GetNoteResp,
  Note,
  POST_FILTER,
  GetNotePreviewResp,
} from "@types"
import customFetch from "./helper"
import { postSlugTag } from "../actions/revalidateTagParams"
import { revalidateBlog, revalidateTag } from "../actions/revalidateTag"

const API = "/notes"

/**
 * Fetch the latest notes. Only fetches the minimal fields (NOT JSON or HTML),
 * which means it's fast. Used when populating the writer dashboard.
 *
 * By default fetches everything, but if `filter` is specified, narrows it
 * to only those posts.
 */
export async function getLatestNotes(
  limit = 10,
  offset = 0,
  filter: POST_FILTER
): Promise<Array<GetNoteResp>> {
  const query = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
    filter: filter,
  })

  const response = await customFetch<Array<GetNoteResp>>(
    API + "/latest/preview?" + query
  )

  return response
}

/**
 * Gets a count of all "latest notes" a user has.
 *
 * This is used for pagination.
 */
export async function getLatestNoteCount(
  filter?: POST_FILTER
): Promise<number> {
  const query = new URLSearchParams({
    filter: filter || "",
  })
  const response = await customFetch<number>(
    API + "/latest/preview/count?" + query
  )
  return response
}

/**
 * Gets the writers post. This is ONLY for logged-in writers.
 *
 * This fetches whatever post you specify in the id.
 */
export async function getWritersPost(noteId: string): Promise<Note> {
  const response = await customFetch<Note>(API + `/${noteId}`, {
    cache: "no-store",
  })
  return response
}

/**
 * Gets the writers post's metadata, by an ID.
 * This only fetches the metadata, not the full post, so no post JSON or HTML.
 */
export async function getWritersPostMetadata(
  noteId: string
): Promise<GetNotePreviewResp> {
  const response = await customFetch<GetNotePreviewResp>(
    API + `/${noteId}/minimal`,
    {
      cache: "no-store",
    }
  )
  return response
}

/**
 * Gets the writers post. This is ONLY for logged-in writers.
 *
 * This fetches the latest post, meaning if the post has been published and then has a draft, this'll fetch the draft.
 * You can pass in any post id from a related group of posts (e.g. any draft or published version of a post) and this'll
 * get the latest draft.
 */
async function getWritersPostLatest(noteId: string): Promise<Note> {
  const response = await customFetch<Note>(API + `/${noteId}/latest`, {
    cache: "no-store",
  })
  return response
}

/**
 * Gets the writers post. This is ONLY for logged-in writers.
 *
 * This fetches the PUBLISHED post, meaning if the post has been published and then has a draft, this'll fetch the public published version.
 * You can pass in any post id from a related group of posts (e.g. any draft or published version of a post) and this'll
 * get the latest published version.
 */
async function getWritersPostPublished(noteId: string): Promise<Note> {
  const response = await customFetch<Note>(API + `/${noteId}/published`, {
    cache: "no-store",
  })
  return response
}

/**
 * Gets the writers post. This is ONLY for logged-in writers.
 *
 * This fetches the FIRST published post (the original published version) by passing in any post id from a group of related posts.
 */
async function getWritersPostPublishedOriginal(noteId: string): Promise<Note> {
  const response = await customFetch<Note>(
    API + `/${noteId}/published-original`,
    {
      cache: "no-store",
    }
  )
  return response
}

const isSavingPost: boolean | undefined = undefined

/**
 * Updates a post.
 *
 * Pass ONLY the changed params to the Post object.
 */
export async function updatePost({
  postId,
  post,
  isSavingCallback,
  blogSlug,
  customDomain,
}: {
  postId: string
  post: Partial<Note>
  isSavingCallback?: (isSaving: boolean) => void
  blogSlug?: string
  customDomain?: string
}): Promise<void> {
  isSavingCallback?.(true)
  // If the user is manually setting updatedAt, don't override it.
  if (!post.updatedAt) {
    post.updatedAt = Date.now()
  }

  await customFetch(API + `/${postId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(post),
  })

  // Revalidate the blog for certain post updates
  if (
    blogSlug &&
    (post.title ||
      post.published ||
      "archived" in post ||
      "isUnlisted" in post ||
      post.slug)
  ) {
    console.log("Revalidating blog", blogSlug, customDomain)
    await revalidateBlog(blogSlug, customDomain)
  }

  isSavingCallback?.(false)

  return
}

/**
 * Helper function to archive a post.
 *
 * Updates it with the required params.
 */
export async function archivePost({
  blogSlug,
  customDomain,
  postId,
}: {
  blogSlug: string
  customDomain?: string
  postId: string
}): Promise<void> {
  const post = {} as Partial<Note>

  post.updatedAt = Date.now()
  post.archived = true
  post.draftOf = null

  await updatePost({ postId, post, blogSlug, customDomain })

  return
}

/**
 * Helper function to unarchive a post.
 *
 * Updates it with the required params.
 */
export async function unarchivePost({
  postId,
}: {
  postId: string
}): Promise<void> {
  const post = {} as Partial<Note>

  post.updatedAt = Date.now()
  post.archived = false

  await updatePost({ postId, post })

  return
}

/**
 * Deletes a post.
 */
export async function deletePost(postId: string): Promise<boolean> {
  const res = await customFetch<{ note: Note }>(`${API}/${postId}`, {
    method: "DELETE",
  })

  if (isErrorResponse(res)) {
    console.error("Failed to undo draft.", res.msg)
    return false
  }

  return true
}

export async function newPost(
  blogSlug: string,
  customDomain?: string,
  draftOf?: Partial<GetNoteResp>
): Promise<string> {
  const post = await customFetch<{ note: Note }>(API, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(draftOf),
  })

  revalidateBlog(blogSlug, customDomain)

  return post.note.id
}

type PublishPostParams = {
  blogSlug: string
  postId: string
  post: Partial<Note>
  draftOf?: string | null
  isPublished: boolean
  sendNewsletter: boolean
  scheduledAt?: number
}

type PublishPostResult = {
  success: boolean
  message?: string
}

/**
 * Publishes a post immediately or schedules it for later.
 *
 * @param params - The parameters for publishing or scheduling a post.
 * @returns A promise that resolves to an object indicating success and a message.
 * @throws Will throw an error if the API call fails.
 */

export async function publishPostNowOrLater(
  params: PublishPostParams
): Promise<PublishPostResult> {
  const {
    blogSlug,
    postId,
    post,
    isPublished,
    draftOf,
    sendNewsletter,
    scheduledAt,
  } = params

  // Prepare post publishing request

  const updatedPostData: Partial<Note> = {
    published: isPublished,
    draftOf,
    sendNewsletter,
    scheduledAt,
    storeOnArweave: true,
    ...post,
  }

  // Update post with publishing data

  try {
    await updatePost({ postId, post: updatedPostData, blogSlug })

    if (post.slug) {
      revalidateTag(postSlugTag(post.slug))
    } else {
      console.error("No slug found in post data; unable to revalidate tag.")
    }

    return {
      success: true,
      message: scheduledAt
        ? "Post scheduled successfully"
        : isPublished
          ? "Post published successfully"
          : "Post updated successfully",
    }
  } catch (error) {
    console.error("Error in publishPostNowOrLater:", error)
    return {
      success: false,
      message: "Failed to publish or schedule post",
    }
  }
}

/**
 * Sends a test newsletter for the given post.
 */
export async function sendTestNewsletter(
  postId: string
): Promise<{ success: boolean; message?: string }> {
  try {
    await customFetch(API + `/send-test/${postId}`, {
      method: "POST",
    })

    return { success: true, message: "Test newsletter sent successfully" }
  } catch (error) {
    console.error("Error sending test newsletter:", error)
    return { success: false, message: "Failed to send test newsletter" }
  }
}

export async function uploadImage(
  file: File | Blob
): Promise<ImageWithPlaceholder | undefined> {
  const formData = new FormData()
  formData.append("file", file)

  try {
    const response = await customFetch<
      { image: ImageWithPlaceholder } | ErrorResponse
    >(`${API}/image`, {
      method: "POST",
      body: formData,
      // The custom fetch function in helper.ts already handles credentials and headers.
    })

    if (isErrorResponse(response)) {
      console.error("Unable to upload image:", response.msg)
      return undefined
    }

    return response.image
  } catch (error) {
    console.error("Error uploading image:", error)
    return undefined
  }
}

export async function getThirdPartyImage(
  imageUrl: string
): Promise<ImageWithPlaceholder | undefined> {
  try {
    const query = new URLSearchParams({ imageUrl }).toString()
    const response = await customFetch<
      { image: ImageWithPlaceholder } | ErrorResponse
    >(`${API}/thirdPartyImage?${query}`)

    if (isErrorResponse(response)) {
      console.error("Unable to fetch third-party image:", response.msg)
      return undefined
    }

    return response.image
  } catch (error) {
    console.error("Error fetching third-party image:", error)
    return undefined
  }
}

type ImportNoteResponse =
  | { id: string }
  | { id: "ALREADY_EXISTS" }
  | { id: "FAILURE" }

export async function importNoteExport(
  note: Partial<Note>,
  opts?: {
    overwrite?: boolean
    publish?: boolean
    storeOnArweave?: boolean
  }
): Promise<ImportNoteResponse> {
  try {
    // Convert boolean options to strings for URL parameters
    const stringOpts: Record<string, string> = {}
    if (opts) {
      Object.keys(opts).forEach((key) => {
        const value = opts[key as keyof typeof opts]
        if (value !== undefined) {
          stringOpts[key] = String(value)
        }
      })
    }

    const query = new URLSearchParams(stringOpts).toString()
    const url = `${API}/import${query ? `?${query}` : ""}`

    console.log(`Making API request to ${url} with note:`, {
      title: note.title,
      hasJson: !!note.json,
      hasStaticHtml: !!note.staticHtml,
      options: opts,
    })

    const response = await customFetch<{ id: string } | ErrorResponse>(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(note),
    })

    // Check if the response is a Response object (not parsed JSON)
    if (response instanceof Response) {
      console.log(`Got raw Response with status ${response.status}`)
      if (response.status === 409) {
        return { id: "ALREADY_EXISTS" }
      }
      // Try to parse the response body for more information
      try {
        const textResponse = await response.text()
        console.log(`Response body: ${textResponse}`)
      } catch (err) {
        console.error("Could not read response body:", err)
      }
      return { id: "FAILURE" }
    }

    if (isErrorResponse(response)) {
      console.error("Error importing note:", response.msg)
      return { id: "FAILURE" }
    }

    console.log(`Successfully imported note with ID: ${response.id}`)
    return { id: response.id }
  } catch (error) {
    console.error("Error importing note:", error)
    return { id: "FAILURE" }
  }
}

/**
 * Create a new post from an existing one.
 */
export async function duplicatePost({
  postId,
}: {
  postId: string
}): Promise<string> {
  const duplicatedPost = await customFetch<{ id: string }>(`${API}/duplicate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ id: postId }),
  })

  return duplicatedPost.id
}

/**
 * Deletes all posts for the current blog
 */
export async function deleteAllPosts(): Promise<{
  success: boolean
  message?: string
}> {
  try {
    await customFetch(`${API}/bulk/all`, {
      method: "DELETE",
    })

    return {
      success: true,
      message: "All posts have been deleted successfully",
    }
  } catch (error) {
    console.error("Error deleting all posts:", error)
    return {
      success: false,
      message: "Failed to delete posts. Please try again.",
    }
  }
}

/**
 * Generates a slug for a given title and note ID.
 */
export async function generateSlug(
  title: string,
  noteId: string
): Promise<string> {
  try {
    const response = await customFetch<{ slug: string } | ErrorResponse>(
      `${API}/generate-slug`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ title, noteId }),
      }
    )

    if (isErrorResponse(response)) {
      console.error("Error generating slug:", response.msg)
      return ""
    }

    return response.slug
  } catch (error) {
    console.error("Error generating slug:", error)
    return ""
  }
}
